import React, { FC, useMemo, useState } from 'react';

import { Tooltip } from '@material-ui/core';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { generatePath } from 'react-router-dom';

import ColoredTile from 'components/ColoredTile';
import FooterButton from 'components/FooterButton';
import MobileGuttersContainer from 'components/MobileGuttersContainer';
import Section from 'components/Section';
import Table from 'components/Table';
import TextButton from 'components/TextButton/TextButton';
import TrimHTMLText from 'components/TrimHTMLText/TrimHTMLText';
import TypographyWithHTML from 'components/TypographyWithHTML/TypographyWithHTML';
import UserAvatar from 'components/UserAvatar/UserAvatar';
import assetRiskTasksApi, { legacyAssetRiskTasksApi } from 'config/api/assetRiskTasks/assetRiskTasks';
import { parseAllAssetRiskTasksForFE } from 'config/api/assetRiskTasks/assetRiskTasks.parsers';
import { AssetRiskTasks, AssetRiskTasksStats, ManagementRiskTasksStatus } from 'config/api/assetRiskTasks/assetRiskTasks.types';
import DATE_FORMAT from 'config/constants/DATE_FORMAT';
import pubsub from 'config/events/pubsub';
import usePagination from 'hooks/usePagination';
import usePrepareAvailableFilters from 'hooks/usePrepareAvailableFilters/usePrepareAvailableFilters';
import useSubscription from 'hooks/useSubscription';
import asset_risk_messages from 'messages/asset_risk_messages';
import asset_risk_tasks_messages from 'messages/asset_risk_tasks_messages';
import general_messages from 'messages/general_messages';
import manage_dashboard_messages from 'messages/manage_dasboard_messages';
import { useHelpDialogContext } from 'reactContext/HelpDialogContext/HelpDialogContext';
import assetsImportanceFilter from 'services/_filters/parsers/_assetsFilters/assetsImportanceFilter';
import affectsIndividualsFilter from 'services/_filters/parsers/_riskAnalysisFilters/affectsIndividualsFilter';
import riskPlaceFilter from 'services/_filters/parsers/_riskAnalysisFilters/riskPlaceFilter';
import riskQuadrantFilter from 'services/_filters/parsers/_riskAnalysisFilters/riskQuadrantFilterFactory';
import parseFiltersForRequest from 'services/parseFiltersForRequest';
import { FiltersForRequestT } from 'services/parseFiltersForRequest/parseFiltersForRequest';
import { Id } from 'types/Id';
import { TableDataMappingRow } from 'types/Table';
import { StaticTableFilter } from 'types/TableFilter';

import PATHS from '../../../../router/PATHS';
import useGetManagementReportBaseData from '../../_hooks/useGetManagementReportBaseData';
import ManagementCommentInput from '../ManagementCommentInput/ManagementCommentInput';
import ManagementDashboardKPIGrid from '../ManagementDashboardKPIGrid/ManagementDashboardKPIGrid';
import ManagementDashboardSectionGrid from '../ManagementDashboardSectionGrid/ManagementDashboardSectionGrid';

type Props = {
  reportId?: Id;
};

const ManagementDashboardRiskTasksSection: FC<Props> = ({ reportId }) => {
  const { t } = useTranslation();
  const { showHelpDialog } = useHelpDialogContext();

  const showIntroductionDialog = () => {
    showHelpDialog({
      title: t(manage_dashboard_messages.risk_tasks_section.introduction_title),
      body: t(manage_dashboard_messages.risk_tasks_section.introduction_body),
    });
  };

  const { data } = useGetManagementReportBaseData(reportId);

  const availableFilters = usePrepareAvailableFilters({
    management_status: {
      isDynamic: false,
      sectionName: manage_dashboard_messages.risk_tasks_section.status_filter_label,
      filters: [
        { id: 'all' as ManagementRiskTasksStatus, name: manage_dashboard_messages.risk_tasks_section.status_filter_all },
        { id: 'ongoing' as ManagementRiskTasksStatus, name: manage_dashboard_messages.risk_tasks_section.status_filter_ongoing },
        { id: 'completed' as ManagementRiskTasksStatus, name: manage_dashboard_messages.risk_tasks_section.status_filter_completed },
      ],
      isSingleSelect: true,
    } as StaticTableFilter,
    risk__risk_quadrant: riskQuadrantFilter(asset_risk_messages.risk_table.calculated_risk_filter),
    risk__affect_individuals: affectsIndividualsFilter,
    risk__risk_analysis__asset__info__assets_importance: assetsImportanceFilter,
    risk__place: riskPlaceFilter,
  });

  const baseFiltersForRequest = useMemo<FiltersForRequestT>(() => {
    const requestFilters: FiltersForRequestT = {};
    if (!reportId || !data) return requestFilters;

    requestFilters.management_report = `${reportId}`;
    requestFilters.date_from = format(data.dateFrom, DATE_FORMAT.defaultDate);
    requestFilters.date_to = format(data.dateTo, DATE_FORMAT.defaultDate);
    requestFilters.management_status = 'all';

    return requestFilters;
  }, [data, reportId]);

  // TABLE LOGIC
  const [sort, setSort] = useState<{ key: string | null; direction: string | null }>({ key: null, direction: null });
  const onSort = (key: string, direction: string) => setSort({ key, direction });
  const [filters, setFilters] = useState({});
  const filtersForRequest = useMemo(
    () => parseFiltersForRequest(filters, baseFiltersForRequest, availableFilters || {}),
    [availableFilters, baseFiltersForRequest, filters],
  );

  const { data: riskTasksStats } = useQuery(
    ['managementReportRiskTasksStats', filtersForRequest],
    () => assetRiskTasksApi.getManagementReportRiskTasksStats(filtersForRequest),
    {
      enabled: !!reportId && !!data,
      placeholderData: {
        overdueTasks: 0,
        notStartedTasks: 0,
        startedTasks: 0,
        finishedTasks: 0,
      } as AssetRiskTasksStats,
    },
  );

  const kpiData = useMemo(
    () => [
      {
        description: t(manage_dashboard_messages.risk_tasks_section.not_started_tasks_kpi_label),
        title: riskTasksStats?.notStartedTasks ?? null,
      },
      {
        description: t(manage_dashboard_messages.risk_tasks_section.started_tasks_kpi_label),
        title: riskTasksStats?.startedTasks ?? null,
      },
      {
        description: t(manage_dashboard_messages.risk_tasks_section.finished_tasks_kpi_label),
        title: riskTasksStats?.finishedTasks ?? null,
      },
      {
        description: t(manage_dashboard_messages.risk_tasks_section.overdue_tasks_kpi_label),
        title: riskTasksStats?.overdueTasks ?? null,
      },
    ],
    [riskTasksStats, t],
  );

  const {
    data: tableData,
    getData: getTableData,
    hasNextPage,
    loading,
    initialLoading,
    refreshData,
  } = usePagination({
    endpointFunc: legacyAssetRiskTasksApi.getAllAssetRiskTasks('managementReport'),
    limits: [5, 5, 5],
    isGet: true,
    dependencies: [filters as never, baseFiltersForRequest as never],
    sort,
    filters: filtersForRequest,
    disabled: !data,
  });

  useSubscription(pubsub.SELECTED_MANAGEMENT_REPORTS_UPDATED, refreshData);

  const dataMapping: TableDataMappingRow<AssetRiskTasks>[] = useMemo(
    () => [
      {
        label: t(asset_risk_tasks_messages.table.asset_name_column),
        id: 'risk__risk_analysis__asset__name',
        get: ({ risk }) => risk.riskAnalysis.asset.name,
        width: '15%',
      },
      {
        label: t(asset_risk_tasks_messages.table.assigned_column),
        id: 'assigned_to__full_name',
        get: ({ assignedTo }) => <UserAvatar showName userData={assignedTo.profile} userId={assignedTo.id} />,
        width: '18%',
      },
      {
        label: t(asset_risk_tasks_messages.table.analysis_date_column),
        id: 'risk__risk_analysis__date',
        get: ({ risk }) => format(risk.riskAnalysis.date, DATE_FORMAT.defaultDate),
        width: '12%',
      },
      {
        label: t(asset_risk_tasks_messages.table.risk_name_column),
        id: 'risk__name',
        get: ({ risk }) => risk.name,
        width: '17%',
      },
      {
        id: 'planned_action',
        label: t(asset_risk_tasks_messages.table.planned_action_column),
        get: ({ description, plannedAction }) => (
          <Tooltip title={description ? <TypographyWithHTML>{description}</TypographyWithHTML> : ''}>
            <span>
              <TrimHTMLText linesToShow={2}>{plannedAction}</TrimHTMLText>
            </span>
          </Tooltip>
        ),
        width: '18%',
      },
      {
        label: t(asset_risk_tasks_messages.table.due_date_column),
        id: 'due_date',
        get: ({ dueDate }) => format(dueDate, DATE_FORMAT.defaultDate),
        width: '10%',
      },
      {
        label: t(asset_risk_tasks_messages.table.status_column),
        id: 'status',
        get: ({ lastStatusDate, status }) => (
          <Tooltip title={format(lastStatusDate, DATE_FORMAT.defaultDate)}>
            <span>{t(asset_risk_messages.risk_task_status[status])}</span>
          </Tooltip>
        ),
        width: '10%',
      },
    ],
    [t],
  );

  const getRowHref = ({ risk }: AssetRiskTasks) => generatePath(PATHS.RISK_ANALYSIS_DETAIL, { riskAnalysisId: risk.riskAnalysis.id });

  return (
    <Section
      title={manage_dashboard_messages.risk_tasks_section.title}
      titleLineAddon={[
        <TextButton key='introduction' onClick={showIntroductionDialog}>
          {t(manage_dashboard_messages.risk_tasks_section.introduction_button)}
        </TextButton>,
      ]}
      // @ts-ignore
      titleWrapper={MobileGuttersContainer}
    >
      <ManagementDashboardSectionGrid>
        <ManagementDashboardKPIGrid>
          {kpiData.map(({ description, title }) => (
            <ColoredTile key={description} centered description={description} disableRipple isLoading={title === null} title={title} />
          ))}
        </ManagementDashboardKPIGrid>
        <div>
          <Table
            clickableRow
            data={tableData && parseAllAssetRiskTasksForFE(tableData)}
            dataMapping={dataMapping}
            filtersService={{ availableFilters, filters, setFilters }}
            fixedHeader
            getRowHref={getRowHref}
            onSort={onSort}
            openInNewTab
            refetching={loading && !initialLoading}
            sort={sort}
          />
          {hasNextPage && (
            // @ts-ignore
            <FooterButton loading={loading} onClick={getTableData}>
              {t(general_messages.show_more)}
            </FooterButton>
          )}
        </div>
        <MobileGuttersContainer>
          <ManagementCommentInput fieldKey='riskTasksReport' initialValue={data?.riskTasksReport.comment || ''} reportId={reportId} />
        </MobileGuttersContainer>
      </ManagementDashboardSectionGrid>
    </Section>
  );
};

export default ManagementDashboardRiskTasksSection;
